import React, { ChangeEventHandler, useEffect, useRef, useState } from 'react';

import { FormControl, FormControlProps, IconButton, InputAdornment, InputLabel, OutlinedInput } from '@material-ui/core';
import { Clear as ClearIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import useDebounce from 'hooks/useDebounce';
import search_messages from 'messages/search_messages';

type Props = {
  label: string;
  action: (param: string) => void;
  formControl?: FormControlProps;
  className?: string;
  value?: string;
};

const DEBOUNCE_TIME = 500;

const InputWithDebouncedAction: React.FC<Props> = ({ label, action, className, formControl = {}, value: externalValue }) => {
  const { t } = useTranslation();
  const isControlled = externalValue !== undefined;

  // W controlled mode używamy lokalnego stanu dla responsywności UI
  // W uncontrolled mode używamy lokalnego stanu normalnie
  const [displayValue, setDisplayValue] = useState(isControlled ? externalValue || '' : '');

  // Ref do śledzenia ostatniej wartości zewnętrznej, aby wykryć zmiany zewnętrzne (np. reset)
  const lastExternalValueRef = useRef(externalValue);
  // Ref do śledzenia ostatniej wartości debounced, aby uniknąć synchronizacji gdy zmiana pochodzi z debounce
  const lastDebouncedValueRef = useRef(displayValue);
  // Ref do przechowywania action, aby uniknąć ponownych wywołań przy zmianie referencji
  const actionRef = useRef(action);

  // Aktualizuj actionRef przy każdej zmianie action
  useEffect(() => {
    actionRef.current = action;
  }, [action]);

  // Debounce dla wywołania action
  const debouncedDisplayValue = useDebounce(displayValue, DEBOUNCE_TIME);

  // Synchronizacja z value prop tylko gdy zmienia się zewnętrznie (np. reset)
  // Nie synchronizujemy gdy zmiana pochodzi z wewnątrz (z debounce)
  useEffect(() => {
    if (isControlled && externalValue !== lastExternalValueRef.current) {
      // Sprawdź czy zmiana nie pochodzi z debounce (gdy externalValue === ostatnia debounced wartość)
      if (externalValue !== lastDebouncedValueRef.current) {
        // Zmiana zewnętrzna (np. reset) - synchronizuj
        setDisplayValue(externalValue || '');
      }
      lastExternalValueRef.current = externalValue;
    }
  }, [externalValue, isControlled]);

  const updateValue: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    const newValue = target.value;
    setDisplayValue(newValue);
  };

  const clearValue = () => {
    setDisplayValue('');
    lastDebouncedValueRef.current = '';
    lastExternalValueRef.current = '';
    actionRef.current('');
  };

  // Wywołanie action z debounce
  useEffect(() => {
    // Wywołaj action tylko jeśli wartość faktycznie się zmieniła
    if (debouncedDisplayValue !== lastDebouncedValueRef.current) {
      // Aktualizuj ref przed wywołaniem action
      lastDebouncedValueRef.current = debouncedDisplayValue;
      actionRef.current(debouncedDisplayValue);
    }
  }, [debouncedDisplayValue]);

  return (
    <FormControl className={className} fullWidth variant='outlined' {...formControl}>
      <InputLabel color='secondary' htmlFor='search'>
        {label}
      </InputLabel>
      <OutlinedInput
        color='secondary'
        data-testid='debounced-search'
        endAdornment={
          displayValue ? (
            <InputAdornment position='end'>
              <IconButton
                aria-label={t(search_messages.clear_search)}
                data-testid='debounced-search-clear'
                edge='end'
                onClick={clearValue}
                size='small'
              >
                <ClearIcon fontSize='small' />
              </IconButton>
            </InputAdornment>
          ) : null
        }
        id='search'
        label={label}
        onChange={updateValue}
        value={displayValue}
      />
    </FormControl>
  );
};

export default InputWithDebouncedAction;
